import type { IViaCepResponse } from '@/interfaces';
import { StringMask } from '@/utils/StringMask';
import axios from 'axios';
import { NInput } from 'naive-ui';
import { defineComponent, ref } from 'vue';

const MAX_CEP_LENGTH = 8;

export const AppInputCep = defineComponent({

	props: {
		modelValue: String,
	},
	emits: ['update:modelValue', 'address'],

	setup(props, { emit }) {
		const http = axios.create({ baseURL: 'https://viacep.com.br/ws' });
		const cepMask = new StringMask({ mask: '00000-000' });

		// CEPs são armazenados como número, então zeros à esquerda se perdem (ex.: 03160010 -> 3160010).
		// Normaliza para dígitos e faz padStart(8) antes de aplicar a máscara para não exibir "31600-10".
		const maskedValue = ref(
			props.modelValue
				? cepMask.apply(
						String(props.modelValue).replace(/\D/g, '').padStart(MAX_CEP_LENGTH, '0'),
					).maskedValue
				: '',
		);
		const loading = ref(false);

		async function getAddress(cep: string) {
			try {
				loading.value = true;
				const { data } = await http.get<IViaCepResponse>(
					`/${cep.padStart(8, '0')}/json`,
				);
				emit('address', data);
			} catch {
			} finally {
				loading.value = false;
			}
		}

		function handleInput(value: string) {
			const response = cepMask.apply(value);
			const unmasked = response.unmaskedValue.replace(/\D/g, '');
			maskedValue.value = response.maskedValue;

			if (unmasked.length === 0) {
				emit('update:modelValue', '');
				return;
			}

			if (unmasked.length === MAX_CEP_LENGTH) {
				// Preserve left zeros by keeping CEP as string
				const normalizedCep = unmasked.padStart(8, '0');
				emit('update:modelValue', normalizedCep);
				getAddress(normalizedCep);
			}
		}

		return () => (
			<NInput
				inputProps={{
					autocomplete: 'postal-code',
					inputmode: 'numeric',
				}}
				placeholder="00000-000"
				maxlength="9"
				loading={loading.value}
				onInput={handleInput}
				v-model:value={maskedValue.value}
			/>
		);
	},
});

export default AppInputCep;
